import { useCallback, useMemo } from 'react';
import type { SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SecondaryNavigationActionButton,
  SecondaryNavigationIconButton,
  SecondaryNavigationInfoLabel,
  SecondaryNavigation as SecondaryNavigationPrimitive,
  SecondaryNavigationSelector,
  SecondaryNavigationSkeleton,
} from 'components/ui';
import { useFirmBaseLayoutContext } from 'context/FirmBaseLayoutContext';
import type {
  SecondaryNavigationActionControlConfig,
  SecondaryNavigationTitleAccessoryControlConfig,
} from 'context/FirmBaseLayoutContext';
import { useZoom } from 'context/ZoomContext';

const getPathnameFromNavigationTarget = (to: string) => to.split('?')[0] || to;

const getSecondaryNavigationControlKey = (
  control: SecondaryNavigationActionControlConfig | SecondaryNavigationTitleAccessoryControlConfig,
  index: number
) => control.key ?? `${control.type}-${index}`;

const renderSecondaryNavigationControl = (
  control: SecondaryNavigationActionControlConfig | SecondaryNavigationTitleAccessoryControlConfig,
  index: number
) => {
  const key = getSecondaryNavigationControlKey(control, index);

  switch (control.type) {
    case 'button':
      return (
        <SecondaryNavigationActionButton
          key={key}
          data-testid={control.key}
          disabled={control.disabled}
          endIcon={control.endIcon}
          errorLabel={control.errorLabel}
          isSaving={control.isSaving}
          onClick={control.onClick}
          saveFailed={control.saveFailed}
          savedLabel={control.savedLabel}
          savingLabel={control.savingLabel}
          startIcon={control.startIcon}
          data-tour={control.dataTour}
          variant={control.variant}>
          {control.label}
        </SecondaryNavigationActionButton>
      );
    case 'iconButton':
      return (
        <SecondaryNavigationIconButton
          key={key}
          active={control.active}
          ariaLabel={control.ariaLabel}
          disabled={control.disabled}
          icon={control.icon}
          onClick={control.onClick}
          data-tour={control.dataTour}
          tooltipTitle={control.tooltipTitle}
          variant={control.variant}
        />
      );
    case 'infoLabel':
      return (
        <SecondaryNavigationInfoLabel
          key={key}
          data-tour={control.dataTour}
          label={control.label}
          value={control.value}
          valueTone={control.valueTone}
        />
      );
    case 'selector':
      return (
        <SecondaryNavigationSelector
          key={key}
          disabled={control.disabled}
          endIcon={control.endIcon}
          label={control.label}
          onClick={control.onClick}
          startIcon={control.startIcon}
          data-tour={control.dataTour}
          value={control.value}
          variant={control.variant}
        />
      );
    default:
      return null;
  }
};

const renderSecondaryNavigationControls = (controls: SecondaryNavigationActionControlConfig[] = []) =>
  controls.map(renderSecondaryNavigationControl);

interface FirmBaseSecondaryNavigationProps {
  'data-tour'?: string;
}

const SecondaryNavigation = (rootProps: FirmBaseSecondaryNavigationProps) => {
  const { secondaryNavigation } = useFirmBaseLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { zoomLevel } = useZoom();

  const items = useMemo(() => secondaryNavigation?.items ?? [], [secondaryNavigation?.items]);

  const activeValue = useMemo(() => {
    // Allow pages to override URL-derived matching — required when items represent
    // parent tabs whose URLs are prefixes of the current nested route (e.g. a tab
    // pointing at /intelligence/summaries must remain active under /intelligence/summaries/view-3).
    if (secondaryNavigation?.value !== undefined) {
      return secondaryNavigation.value;
    }

    const currentUrl = `${location.pathname}${location.search}`;
    const activeItem = items.find(item => {
      if (!item.to) {
        return false;
      }

      return item.to === currentUrl
        || getPathnameFromNavigationTarget(item.to) === location.pathname
        || (item.activeFor ?? []).includes(location.pathname);
    });

    return activeItem?.value ?? false;
  }, [items, location.pathname, location.search, secondaryNavigation?.value]);

  const handleChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      const selectedItem = items.find(item => item.value === value);

      if (!selectedItem?.to || selectedItem.disabled) {
        return;
      }

      navigate(selectedItem.to);
    },
    [items, navigate]
  );

  const navigationItems = useMemo(
    () =>
      items.map(item => ({
        disabled: item.disabled || !item.to,
        label: item.label,
        value: item.value,
      })),
    [items]
  );

  const actions = useMemo(() => {
    const actionControls = renderSecondaryNavigationControls(secondaryNavigation?.actions);

    if (!actionControls.length && !secondaryNavigation?.actionsNode) {
      return undefined;
    }

    return (
      <>
        {secondaryNavigation?.actionsNode}
        {actionControls}
      </>
    );
  }, [secondaryNavigation?.actions, secondaryNavigation?.actionsNode]);

  const titleAccessory = useMemo(() => {
    const titleAccessoryControl = secondaryNavigation?.titleAccessory
      ? renderSecondaryNavigationControl(secondaryNavigation.titleAccessory, 0)
      : null;

    if (!titleAccessoryControl && !secondaryNavigation?.titleAccessoryNode) {
      return undefined;
    }

    return (
      <>
        {titleAccessoryControl}
        {secondaryNavigation?.titleAccessoryNode}
      </>
    );
  }, [secondaryNavigation?.titleAccessory, secondaryNavigation?.titleAccessoryNode]);

  if (!secondaryNavigation) {
    return null;
  }

  if (secondaryNavigation.isLoading) {
    return (
      <SecondaryNavigationSkeleton
        ariaLabel={secondaryNavigation.ariaLabel}
        zoomLevel={zoomLevel}
        {...secondaryNavigation.skeletonProps}
      />
    );
  }

  return (
    <SecondaryNavigationPrimitive
      actions={actions}
      ariaLabel={secondaryNavigation.ariaLabel}
      items={navigationItems}
      onChange={handleChange}
      status={secondaryNavigation.status}
      title={secondaryNavigation.title}
      titleAccessory={titleAccessory}
      titleLeading={secondaryNavigation.titleLeading}
      titleTooltip={secondaryNavigation.titleTooltip}
      value={activeValue}
      zoomLevel={zoomLevel}
      {...rootProps}
    />
  );
};

export default SecondaryNavigation;
